import _classnames from 'clsx';
import PropTypes from 'prop-types';
import React, { forwardRef, memo } from 'react';
import Icon from '../icons/Icon/Icon';
import Loader from '../loader/loader';

/**
  Description
*/
function Button({
  buttonProps,
  checkbox,
  children,
  classes,
  disabled,
  fullWidth,
  href,
  iconLeft,
  iconRight,
  iconRightColor,
  innerRef,
  loading,
  onClick,
  onKeyDown,
  onMouseDown,
  small,
  type,
  unstyled,
}) {
  const buttonClasses = _classnames('Button', classes, {
    'Button--checkbox': checkbox,
    'Button--disabled': disabled,
    'Button--full-width': fullWidth,
    'Button--has-icon': iconLeft || iconRight,
    'Button--has-icon-left': iconLeft,
    'Button--has-icon-right': iconRight,
    'Button--icon-only': (iconLeft || iconRight) && (children === '' || children === null),
    'Button--loading': loading,
    'Button--small': small,
    [`Button--${type}`]: type && !loading,
    'Button--unstyled': unstyled,
    'Button--isLink': href,
  });

  const buttonTypeToColor = {
    primary: '#ffffff',
    transactional: '#ffffff',
    secondary: disabled ? '#969fb1' : '#8008f7',
    destructiveMajor: '#ffffff',
    destructiveMinor: disabled ? '#fbc7d2' : '#c23754',
    reversed: disabled ? '#969fb1' : '#ffffff',
    minimal: '#8008f7',
  };
  let fill = buttonTypeToColor[type];
  if (loading) {
    fill = '#969fb1';
  }
  const iconRightFill = iconRightColor || fill;
  const content = (
    <div className="Button__content">
      {checkbox}
      {iconLeft ? (
        <div className="Button__icon Button__icon--left">
          <Icon name={iconLeft} size="minor" svgProps={{ style: { fill } }} />
        </div>
      ) : null}
      {children && <span className="Button__text">{children}</span>}
      {iconRight ? (
        <div className="Button__icon Button__icon--right">
          <Icon name={iconRight} size="minor" svgProps={{ style: { fill: iconRightFill } }} />
        </div>
      ) : null}
    </div>
  );

  return href ? (
    <a
      className={buttonClasses}
      href={href}
      onMouseDown={onMouseDown}
      rel="noreferrer"
      target="_blank"
      {...buttonProps}
    >
      {content}
    </a>
  ) : (
    <button
      className={buttonClasses}
      disabled={disabled || loading}
      onClick={disabled ? null : onClick}
      onMouseDown={disabled ? null : onMouseDown}
      onKeyDown={disabled ? null : onKeyDown}
      ref={innerRef}
      type="button"
      {...buttonProps}
    >
      {loading ? <Loader classes="Button__loader" isLoading /> : null}
      {content}
    </button>
  );
}

Button.propTypes = {
  /**
    Description
  */
  // eslint-disable-next-line react/forbid-prop-types
  buttonProps: PropTypes.object,
  /**
    Description
  */
  checkbox: PropTypes.node,
  /**
    Description
  */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.string,
  ]),
  /**
    Description
  */
  classes: PropTypes.string,
  /**
    Description
  */
  disabled: PropTypes.bool,
  /**
    Description
  */
  fullWidth: PropTypes.bool,
  /**
    Description
  */
  href: PropTypes.string,
  /**
    Description
  */
  iconLeft: PropTypes.string,
  /**
    Description
  */
  iconRight: PropTypes.string,
  /**
    Description
  */
  iconRightColor: PropTypes.string,
  /**
    Description
  */
  loading: PropTypes.bool,
  /**
    Description
  */
  onClick: PropTypes.func,
  /**
    Description
  */
  onKeyDown: PropTypes.func,
  /**
    Description
  */
  onMouseDown: PropTypes.func,
  /**
    Description
  */
  small: PropTypes.bool,
  /**
    Description
  */
  submit: PropTypes.bool,
  /**
    Description
  */
  type: PropTypes.oneOf([
    'primary',
    'transactional',
    'secondary',
    'destructiveMajor',
    'destructiveMinor',
    'reversed',
  ]).isRequired,
  /**
   * Inner Ref
   */
  innerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  /**
   * Remove styling
   */
  unstyled: PropTypes.bool,
};

Button.defaultProps = {
  buttonProps: null,
  checkbox: null,
  children: null,
  classes: '',
  disabled: false,
  fullWidth: false,
  href: null,
  iconLeft: null,
  iconRight: null,
  loading: false,
  onClick: null,
  onMouseDown: null,
  onKeyDown: null,
  small: false,
  submit: false,
  iconRightColor: '',
  innerRef: null,
  unstyled: false,
};

/**
 * @type {React.ComponentType<any>}
 */
export default memo(forwardRef((props, ref) => <Button innerRef={ref} {...props} />));
